import React, { useState, ChangeEvent, useEffect } from 'react';
import styles from './Step2.module.scss';
import delIcon from '@/assets/images/路径 1794@2x.png';
import { TimePicker, message, Upload, Modal, InputNumber } from 'antd';
import type { UploadProps, UploadFile } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
interface AttributeItem {
  name: string;
  img: string;
  is_on_shelf: boolean;
  file_id?: number | null;
}
interface SpecItem {
  name: string;
  price: number | '';
  duration: number | '';
  is_on_shelf: boolean;
}
 
interface Step2Props {
  onNext: (data: any) => void;
  onPrev: () => void;
  defaultValue?: any;
}

const Step2: React.FC<Step2Props> = ({ onNext, onPrev, defaultValue }) => {
  const { t, i18n } = useTranslation();
  const [attributes, setAttributes] = useState<AttributeItem[]>(
    (defaultValue?.attributes && defaultValue.attributes.length > 0)
      ? defaultValue.attributes
      : [{ name: '', img: '', is_on_shelf: true, file_id: null }]
  );
  const [specs, setSpecs] = useState<SpecItem[]>(
    (defaultValue?.specs && defaultValue.specs.length > 0)
      ? defaultValue.specs
      : [{ name: '', price: '', duration: '', is_on_shelf: true }]
  );
  const [reservationTimes, setReservationTimes] = useState<string[]>(
    (defaultValue?.reservationTimes && defaultValue.reservationTimes.filter(Boolean).length > 0)
      ? defaultValue.reservationTimes.filter(Boolean)
      : [''] 
  );

  // 获取token
  const token = useAppSelector((state) => state.user.token);

  useEffect(() => {
    if (defaultValue) {
      if (defaultValue.attributes) setAttributes(defaultValue.attributes);
      if (defaultValue.specs) setSpecs(defaultValue.specs);
      if (defaultValue.reservationTimes) setReservationTimes(defaultValue.reservationTimes);
    }
  }, [defaultValue]);

  // Add attribute
  const handleAddAttr = () => {
    setAttributes(attrs => [...attrs, { name: '', img: '', is_on_shelf: true, file_id: null }]);
    setSpecs(specs => [...specs, { name: '', price: '', duration: '', is_on_shelf: true }]);
  };
  // Edit attribute name
  const handleAttrNameChange = (idx: number, val: string) => {
    setAttributes(attrs => attrs.map((a, i) => i === idx ? { ...a, name: val } : a));
    setSpecs(specs => specs.map((s, i) => i === idx ? { ...s, name: val } : s));
  };

  // Delete attribute
  const handleDeleteAttr = (idx: number) => {
    setAttributes(attrs => attrs.filter((_, i) => i !== idx));
    setSpecs(specs => specs.filter((_, i) => i !== idx));
  };
  // Whether to list开关
  const handleListedChange = (idx: number, val: boolean) => {
    setAttributes(attrs => attrs.map((a, i) => i === idx ? { ...a, is_on_shelf: val } : a));
    setSpecs(specs => specs.map((s, i) => i === idx ? { ...s, is_on_shelf: val } : s));
  };
  // Price/Duration input
  const handleSpecInput = (idx: number, field: 'price' | 'duration', value: string | number | null) => {
    let finalValue: number | '';
    
    if (value === '' || value === null || value === undefined) {
      finalValue = ''; // 保持为空字符串
    } else if (typeof value === 'string') {
      finalValue = value === '' ? '' : (parseFloat(value) || 0);
    } else {
      finalValue = value;
    }
    
    setSpecs(specs => specs.map((s, i) => i === idx ? { ...s, [field]: finalValue } : s));
  };
  // Delete spec (sync attr)
  const handleDeleteSpec = (idx: number) => {
    handleDeleteAttr(idx);
  };
  // 添加预约时间
  const handleAddTime = () => {
    setReservationTimes(times => [...times, '']);
  };
  // 编辑预约时间
  const handleTimeChange = (idx: number, dates: any, dateStrings: string[]) => {
    // 确保时间字符串格式正确
    if (dateStrings[0] && dateStrings[1]) {
      setReservationTimes(times => times.map((t, i) => i === idx ? `${dateStrings[0]} - ${dateStrings[1]}` : t));
    }
  };
  // 删除预约时间
  const handleDeleteTime = (idx: number) => {
    setReservationTimes(times => times.filter((_, i) => i !== idx));
  };

  // 图片上传配置
  const createImageProps = (idx: number): UploadProps => ({
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    accept: ".png,.jpeg,.jpg,.gif",
    headers: {
      token: `${token}`,
    },
    listType: 'picture-card',
    maxCount: 1, // 每个属性只允许上传一张图片
    showUploadList: false,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("Image must be smaller than 10MB!");
      }
      return isLt10M; // 返回 false 阻止上传
    }, 
    onChange(info) {
      if (info.file.status !== 'uploading') {
        console.log(info.file, info.fileList);
      }

      if (info.file.status === 'done') {
        // 更新图片URL和file_id
        if (info.file.response?.data?.url) {
          setAttributes(attrs => attrs.map((a, i) => 
            i === idx ? { 
              ...a, 
              img: info.file.response.data.url,
              file_id: info.file.response.data.file_id || null
            } : a
          ));
        }
      }
    },
    async onPreview(file: UploadFile) {
      // 预览逻辑
      let src = file.url as string;
      if (!src) {
        src = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj as File);
          reader.onload = () => resolve(reader.result as string);
        });
      }

    }
  });

  const handleNext = () => {
    // 校验 Sales Attributes 和 Sales Specifications
    const validationErrors: string[] = [];
    
    // 检查已添加的属性是否完整
    attributes.forEach((attr, index) => {
      if (!attr.name.trim()) {
        validationErrors.push(`t("Step2.SalesAttribute") ${index + 1}: t("Step2.enterattributename")`);
      }
      if (!attr.img) {
        validationErrors.push(`t("Step2.SalesAttribute") ${index + 1}: t("Step2.uploadimage")`);
      }
    });
    
    // 检查已添加的规格是否完整
    specs.forEach((spec, index) => {
      if (spec.price === '' || spec.price === null || spec.price === undefined) {
        validationErrors.push(`t("Step2.SalesSpecification") ${index + 1}: t("Step2.enterminimumprice")`);
      }
      if (spec.duration === '' || spec.duration === null || spec.duration === undefined) {
        validationErrors.push(`t("Step2.SalesSpecification") ${index + 1}:  t("Step2.enterestimatedduration")`);
      }
    });
    
    // 如果有校验错误，显示错误信息并阻止进入下一步
    if (validationErrors.length > 0) {
      message.error(validationErrors.join('\n'));
      return;
    }
    
    // 校验预约时间必填
    if (reservationTimes.length === 0 || reservationTimes.some(time => {
      if (!time) return true;
      const [start, end] = time.split(' - ');
      return !start || !end || start === 'Invalid Date' || end === 'Invalid Date';
    })) {
      message.error(t("Step2.fillreservationtime"));
      return;
    }

    const submitData = { 
      product_id: defaultValue?.product_id || '',
      attributes, 
      specs, 
      reservationOpen: reservationTimes.length > 0,
      reservationTimes 
    };

    onNext(submitData);
  };

  return (
    <div className={styles.step2Container}>
      <h2 className={styles.step2Title}>{t("Step2.SalesInformation")}</h2>
      {/* Sales Attributes */}
      <div className={styles.step2Row}>
        <label className={styles.step2Label}>{t("Step2.SalesAttributes")}</label>
        <div className={styles.step2AttrBox}>
          <div className={styles.step2AttrHeader}>
            <span className={styles.step2AttrTitle}>{t("Step2.AttributeClassification")}</span>
            <button className={styles.step2AttrAddBtn} onClick={handleAddAttr}>{t("Step2.Add")}</button>
          </div>
          <div className={styles.step2AttrList}>
            {attributes.length > 0 ? (
              attributes.map((attr, idx) => (
                <div key={idx} className={styles.step2AttrItem}>
                  <div className={styles.step2AttrImgLabel}>
                    <Upload {...createImageProps(idx)}>
                      {attr.img ? (
                        <img src={attr.img} alt='' className={styles.step2AttrImg} />
                      ) : (
                        <div>
                          <PlusOutlined style={{ fontSize: '16px', color: '#999' }} />
                          <div style={{ marginTop: 4, fontSize: '12px', color: '#999' }}>{t("Step2.Upload")}</div>
                        </div>
                      )}
                    </Upload>
                  </div>
                  <input
                    value={attr.name}
                    onChange={e => handleAttrNameChange(idx, e.target.value)}
                    className={styles.step2AttrInput}
                    placeholder={t("Step2.AttributeClassification")}
                  />
                  <button className={styles.step2AttrDelBtn} onClick={() => handleDeleteAttr(idx)}>
                    <img src={delIcon} alt="delete" className={styles.step2AttrDelIcon} />
                  </button>
                </div>
              ))
            ) : (
              <div className={styles.emptyBox}>
                {t("Step2.nosaleattribute")}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Sales Specifications */}
      <div className={styles.step2Row}>
        <label className={styles.step2Label}>{t("Step2.SalesSpecifications")}</label>
        <div className={styles.step2SpecBox}>
          <table className={styles.step2SpecTable}>
            <thead>
              <tr>
                <th className={styles.step2SpecTh}>{t("Step2.AttributeClassification")}</th>
                <th className={styles.step2SpecTh}>{t("Step2.MinimumPrice")}</th>
                <th className={styles.step2SpecTh}>{t("Step2.EstimatedDuration")}</th>
                <th className={styles.step2SpecTh}>{t("Step2.WhetherToList")}</th>
                <th className={styles.step2SpecTh}>{t("Step2.Operations")}</th>
              </tr>
            </thead>
            <tbody>
              {specs.length > 0 ? (
                specs.map((spec, idx) => (
                  <tr key={idx}>
                    <td className={styles.step2SpecTd}>{spec.name}</td>
                                          <td className={styles.step2SpecTd}>
                        <InputNumber
                          min={0}
                          step={1}
                          className={styles.step2SpecInput}
                          value={spec.price}
                          onChange={(value) => handleSpecInput(idx, 'price', value)}
                        />
                      </td>
                      <td className={styles.step2SpecTd}>
                        <InputNumber
                          min={0}
                          step={1}
                          className={styles.step2SpecInput}
                          value={spec.duration}
                          onChange={(value) => handleSpecInput(idx, 'duration', value)}
                        />
                      </td>
                      <td className={styles.step2SpecTd}>
                       <span
                         className={spec.is_on_shelf ? `${styles.step2Switch} ${styles.step2SwitchOn}` : styles.step2Switch}
                         onClick={() => handleListedChange(idx, !spec.is_on_shelf)}
                       >
                         <span className={styles.step2SwitchCircle}></span>
                       </span>
                     </td>
                    <td className={styles.step2SpecTd}>
                      <button className={styles.step2SpecDelBtn} onClick={() => handleDeleteSpec(idx)}>Delete</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>
                    <div className={styles.emptyBox}>
                    {t("Step2.nosaleattribute")}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Reservation Service */}
      <div className={styles.step2Row}>
        <label className={styles.step2Label}>
          <span className={styles.requiredStar}>*</span>{t("Step2.ReservationService")}
        </label>
        <div className={styles.reservationBox}>
          <div className={styles.reservationTimesBox}>
            {reservationTimes.map((time, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TimePicker.RangePicker
                  className={styles.reservationInput}
                  format="HH:mm"
                  value={time ? [
                    dayjs(time.split(' - ')[0], 'HH:mm'), 
                    dayjs(time.split(' - ')[1], 'HH:mm')
                  ] : undefined}
                  onChange={(dates, dateStrings) => handleTimeChange(idx, dates, dateStrings)}
                  placeholder={["Start", "End"]}
                  allowClear={false}
                />
                {reservationTimes.length > 1 && (
                  <button className={styles.step2AttrDelBtn} onClick={() => handleDeleteTime(idx)}>
                    <img src={delIcon} alt="delete" className={styles.step2AttrDelIcon} />
                  </button>
                )}
              </div>
            ))}
            <button className={styles.reservationAddBtn} onClick={handleAddTime}>{t("Step2.Add")}</button>
          </div>
        </div>
      </div>
      <div className={styles.step2BtnRow}>
        <button type="button" onClick={onPrev} className={`${styles.step2Btn} ${styles.step2BtnCancel}`}>{t("Step2.Cancel")}</button>
        <button type="button" onClick={handleNext} className={styles.step2Btn}>{t("Step2.Next")}</button>
      </div>
    </div>
  );
};

export default Step2;
